import { httpGet, httpPost, httpPostRaw } from "@eres/rest";
import cfg from "./config";
import { RemoteDoc, RVSCompanySearch, RVSDocument, RVSError, Transaction } from "./entities";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { I8N, StringUtil } from "@eres/core";

export enum RVSLookUpName {
	CLAIMANT_TYPES = "ClaimantTypes",
	DEFENDANT_TYPES = "DefendantTypes",
	COMPLAIN_TYPES = "ActivitiesLookup",
}

export const RVSLookUpKeys = {
	[ RVSLookUpName.CLAIMANT_TYPES ]: "claimantTypes",
	[ RVSLookUpName.DEFENDANT_TYPES ]: "defendantTypes",
	[ RVSLookUpName.COMPLAIN_TYPES ]: "complainTypes",
};

export function rvsLookUpToStateKey( lookup: RVSLookUpName ): string {
	if ( RVSLookUpKeys[ lookup ] ) {
		return RVSLookUpKeys[ lookup ];
	}
	return lookup as string;
}

export function loadLookUp( lookUp: RVSLookUpName ) {
	return httpGet( cfg.rvsAPI().endPoint() + `lookup/GetLookUp?lookupName=${ lookUp }` ).then( r => {
		return validateResponse( r );
	} );
}

export function loadComplainCategoriesByType( typeId: number ) {
	return httpGet( cfg.rvsAPI().endPoint() + `lookup/GetLookUp?lookupName=ComplainCategoriesByActivity&parentId=${ typeId }` ).then( r => {
		return validateResponse( r );
	} );
}

export function loadMollakManagementCompany( tabuPropertyId: number ) {
	return httpGet( cfg.mollakMCAPI().endPoint() + `property/${ tabuPropertyId }` );
}

export function loadCompanies( pageIndex: number, pageSize: number, search?: RVSCompanySearch ) {
	let url = cfg.rvsAPI().endPoint() + `authority/searchauthorities?pageIndex=${ pageIndex }&pageSize=${ pageSize }`;
	if ( search ) {
		if ( !isEmptyStringOrNull( search.licenseNo ) ) {
			url += `&licenseNumber=${ search.licenseNo }`;
		}
		if ( !isEmptyStringOrNull( search.companyNameEn ) ) {
			url += `&authorityNameEn=${ search.companyNameEn }`;
		}
		if ( !isEmptyStringOrNull( search.companyNameAr ) ) {
			url += `&authorityNameAr=${ search.companyNameAr }`;
		}
		if ( !isEmptyStringOrNull( search.orn ) ) {
			url += `&realEstateNumber=${ search.orn }`;
		}
	}
	return httpGet( url ).then( r => {
		return validateResponse( r );
	} );
}

export function loadCompaniesByLicenseNo( licenseNo: string ) {
	return httpGet( cfg.rvsAPI().endPoint() + `authority/searchauthorities?&licenseNumber=${ licenseNo }` ).then( r => {
		return validateResponse( r );
	} );
}

export function isRVSUploadUrl( url: string ): boolean {
	return ( url.startsWith( cfg.rvsAPI().endPoint() + "documents/upload" ) );
}

export function uploadDocument( file: File ) {
	const data = new FormData();
	data.append( "document", file, file.name );
	return httpPostRaw( cfg.rvsAPI().endPoint() + "documents/upload", data );
}

export function saveTransaction( request: Transaction ) {
	return httpPost( cfg.rvsAPI().endPoint() + "transactions/savetransaction", request ).then( r => {
		return validateResponse( r );
	} );
}

export function loadComplaints( mobile: string, serviceId: number ) {
	return httpGet( cfg.rvsAPI().endPoint() + `dashboard/searchdashboard?complainerMobile=${ mobile }&serviceId=${ serviceId }` ).then( r => {
		return validateResponse( r );
	} );
}

export function loadComplaintDetails( requestKey: string ) {
	return httpGet( cfg.rvsAPI().endPoint() + `transactions/gettransactiondeatil?requestKey=${ requestKey }` ).then( r => {
		return validateResponse( r );
	} );
}

export function downloadComplaint( requestKey: string ) {
	return httpGet( cfg.rvsAPI().endPoint() + `print/complain/inquiry/pdf/binary?requestKey=${ requestKey }` ).then( r => {
		return validateResponse( r );
	} );
}

export function uploadDocs( docs: RVSDocument[] ): Promise<any> {
	const uploadTasks: Promise<any>[] = [];
	for ( let doc of docs ) {
		if ( doc.remoteDoc ) {
			uploadTasks.push( Promise.resolve( doc.remoteDoc ) );
		} else {
			uploadTasks.push( uploadDocument( doc.localFile! ).then( ( response: any ) => {
				return checkUploadDocErrors( response ).then( ( resp: RemoteDoc[] ) => {
					let remoteDoc = resp[ 0 ];
					doc.remoteDoc = remoteDoc;
					return Promise.resolve( remoteDoc );
				} ).catch( ( reason ) => {
					return Promise.reject( reason );
				} );
			} ).catch( ( reason: any ) => {
				return Promise.reject( reason.toString() );
			} ) );
		}
	}
	return Promise.all( uploadTasks );
}

function validateResponse<T>( response: any ): Promise<T> {
	if ( response == undefined ) {
		return Promise.reject( "Empty response" );
	} else if ( response.Errors && response.Errors.length > 0 ) {
		let reason = getValidationError( response.Errors );
		return Promise.reject( reason );
	}
	return Promise.resolve( response.Response );
}

function getValidationError( errors: RVSError[] ) {
	let errorMsg = errors.map( ( error: RVSError ) => {
		const message = error.Message;
		let nameEn = !StringUtil.isEmptyStringOrNull( message.En ) ? message.En : message.Ar;
		let nameAr = !StringUtil.isEmptyStringOrNull( message.Ar ) ? message.Ar : nameEn;
		return I8N.isRTL() ? nameAr : nameEn;
	} );
	return errorMsg.join( "\n" );
}

function checkUploadDocErrors( response: any ): Promise<any> {
	if ( response.hasOwnProperty( "Errors" ) ) {
		if ( response.Errors && response.Errors.length > 0 ) {
			const errors: RVSError[] = response.Errors;
			let message = "";
			for ( let error of errors ) {
				message += "\n" + ( error.Message ? ( I8N.isRTL() ? error.Message.Ar : error.Message.En ) : "" );
			}
			return Promise.reject( message );
		}
	}
	return Promise.resolve( response.Response );
}
